import { Box } from "@mui/material";

/**
 * FilterTabs — a clean, professional segmented control used for the
 * task-type filters on the Datasets and Learning Models pages.
 *
 * Replaces the scattered, per-type colored pill chips with a single unified
 * container where the active tab reads as a raised white "card". Each tab
 * shows an icon, a label, and a count badge.
 *
 * Props:
 *   tabs:    [{ label, value, count, icon }]
 *   value:   currently-selected value
 *   onChange(value)
 */
const FilterTabs = ({ tabs = [], value, onChange }) => {
  return (
    <Box
      role="tablist"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        bgcolor: 'var(--fx-canvas)',
        border: '1px solid var(--fx-border)',
        borderRadius: 2.5,
        maxWidth: '100%',
        overflowX: 'auto',
        // hide scrollbar but keep scroll on narrow screens
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {tabs.map((tab) => {
        const active = value === tab.value;
        return (
          <Box
            key={tab.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
              userSelect: 'none',
              px: 1.5,
              height: 34,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              bgcolor: active ? 'var(--fx-paper)' : 'transparent',
              color: active ? 'var(--fx-text-primary)' : 'var(--fx-text-secondary)',
              fontWeight: active ? 700 : 600,
              fontSize: '0.8125rem',
              letterSpacing: '-0.005em',
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.10), 0 1px 1px rgba(15,23,42,0.04)' : 'none',
              transition: 'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                color: active ? 'var(--fx-text-primary)' : 'var(--fx-text-secondary)',
                bgcolor: active ? 'var(--fx-paper)' : 'rgba(148,163,184,0.14)',
              },
              '& svg': { fontSize: 16, color: 'inherit' },
            }}
          >
            {tab.icon}
            <Box component="span">{tab.label}</Box>
            {(tab.count !== undefined && tab.count !== null) && (
              <Box
                component="span"
                sx={{
                  minWidth: 19,
                  height: 19,
                  px: 0.6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '999px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  bgcolor: active ? 'rgba(27,54,93,0.10)' : 'var(--fx-border)',
                  color: active ? 'var(--fx-text-primary)' : 'var(--fx-text-secondary)',
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default FilterTabs;
